import { useEffect, useState } from "react";
import { Button, Loader, Select, Skeleton, Textarea, TextInput } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { useClipboard } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import imageCompression from "browser-image-compression";
import ImageUploading, { ImageListType } from "react-images-uploading";
import * as Yup from "yup";
import SettingsShell, { SettingsSection } from "@/modules/settings/SettingsShell";
import classes from "@/modules/settings/settings.module.css";
import merchantService from "@/services/merchant";
import { useGetImage } from "@/utilities/helper/getImage";
import useGetMerchant from "../../hooks/useGetMerchant";
import useUpdateMerchantBusiness from "../../hooks/useUpdateMerchantBusiness";

const schema = Yup.object({
  seoTitle: Yup.string().max(100, "حداکثر ۱۰۰ کاراکتر"),
  seoDescription: Yup.string().max(300, "حداکثر ۳۰۰ کاراکتر"),
  englishName: Yup.string().max(100, "حداکثر ۱۰۰ کاراکتر"),
});
const BUSINESS_TYPES = [
  { value: "hairSalon", label: "آرایشگاه مو" },
  { value: "nailSalon", label: "سالن ناخن" },
  { value: "beautySalon", label: "سالن زیبایی" },
  { value: "spa", label: "اسپا" },
];
type Values = { seoTitle: string; seoDescription: string; englishName: string; businessType: string | null };

const fa = (n: number) => n.toLocaleString("fa-IR");

// «سایت و سئو» (merchant-seo-panel-frontend.md): the site address, how the salon shows in Google, its English
// name and exact type, and the share image. Business fields the page does not edit are sent back unchanged.
function EditSeo() {
  const { data, isLoading, refetch } = useGetMerchant();
  const { updateMerchant, isLoading: saving } = useUpdateMerchantBusiness();
  const clipboard = useClipboard({ timeout: 1500 });
  const info = data?.businessInfo;

  const initial = (): Values => ({
    seoTitle: info?.seoTitle ?? "",
    seoDescription: info?.seoDescription ?? "",
    englishName: info?.englishName ?? "",
    businessType: (info?.businessType as unknown as string | null) ?? null,
  });
  const form = useForm<Values>({ validate: yupResolver(schema), initialValues: initial() });
  useEffect(() => {
    const values = initial();
    form.setValues(values);
    form.resetDirty(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const submit = (values: Values) =>
    updateMerchant(
      {
        title: info?.title ?? "",
        merchantTypeId: info?.merchantType?.id?.toString(),
        subDomain: info?.subDomain || undefined,
        theme: info?.theme,
        voiceType: info?.voiceType,
        // "" clears a field on the server; businessType is only sent once chosen.
        seoTitle: values.seoTitle,
        seoDescription: values.seoDescription,
        englishName: values.englishName,
        businessType: values.businessType || undefined,
      },
      { onSuccess() { notifications.show({ message: "تغییرات ذخیره شد", color: "green" }); form.resetDirty(); refetch(); } },
    );

  // Share image: uploads at once; the server crops it to 1200×630 from the centre.
  const { imageUrl: coverUrl } = useGetImage({ image: data?.cover ?? undefined, bucket: "MerchantCover" });
  const [coverPreview, setCoverPreview] = useState<string>();
  const [coverUploading, setCoverUploading] = useState(false);
  const onCover = async (list: ImageListType) => {
    const file = list[0]?.file;
    if (!file) return;
    if (!["image/jpeg", "image/jpg", "image/png"].includes(file.type)) {
      notifications.show({ message: "لطفاً تصویر JPG یا PNG انتخاب کنید.", color: "red" });
      return;
    }
    setCoverPreview(list[0].data_url);
    setCoverUploading(true);
    try {
      const compressed = await imageCompression(file, { maxSizeMB: 1.5, maxWidthOrHeight: 2400, useWebWorker: true, fileType: file.type, initialQuality: 0.85 });
      await merchantService.updateMerchantCover(new File([compressed], `cover.${file.name.split(".").pop() || "jpg"}`, { type: file.type }));
      notifications.show({ message: "تصویر اشتراک‌گذاری به‌روز شد", color: "green" });
      refetch();
    } catch {
      notifications.show({ message: "بارگذاری تصویر انجام نشد", color: "red" });
      setCoverPreview(undefined);
    } finally {
      setCoverUploading(false);
    }
  };

  const site = info?.subDomain ? `${info.subDomain}.padix.ir` : undefined;
  const title = form.values.seoTitle;
  const text = form.values.seoDescription;
  const cover = coverPreview || coverUrl || undefined;
  const descTone = text.length >= 120 && text.length <= 160 ? "good" : text.length > 160 ? "warn" : undefined;

  return (
    <SettingsShell title="سایت و سئو" description="سالن شما در گوگل و هنگام اشتراک‌گذاری لینک چطور دیده شود." onSubmit={form.onSubmit(submit)} dirty={form.isDirty()} saving={saving} onReset={() => form.reset()}>
      {isLoading ? (
        <><Skeleton height={90} radius={14} /><Skeleton height={260} radius={14} /></>
      ) : (
        <>
          <SettingsSection title="آدرس سایت" description="مشتری‌ها از این آدرس نوبت می‌گیرند و همین لینک در پیامک‌ها فرستاده می‌شود.">
            {site ? (
              <div className={classes.siteRow}>
                <span dir="ltr" className={classes.siteUrl}>{site}</span>
                <Button variant="default" size="sm" onClick={() => clipboard.copy(`https://${site}`)}>{clipboard.copied ? "کپی شد" : "کپی"}</Button>
                <Button variant="default" size="sm" component="a" href={`https://${site}`} target="_blank" rel="noreferrer">باز کردن سایت</Button>
              </div>
            ) : (
              <p className={classes.hint}>هنوز آدرسی برای سایت شما ثبت نشده است. برای ساخت آدرس با پشتیبانی تماس بگیرید.</p>
            )}
          </SettingsSection>

          <SettingsSection title="نتیجه‌ی جستجوی گوگل" description="اگر خالی بماند، نام و معرفی سالن نشان داده می‌شود.">
            <div className={classes.google} aria-label="پیش‌نمایش در گوگل">
              <span className={classes.googleUrl} dir="ltr">{site ?? "your-salon.padix.ir"}</span>
              <span className={classes.googleTitle}>{title || info?.title || "نام مجموعه"}</span>
              <span className={classes.googleText}>{(text || info?.description || "توضیح کوتاهی درباره‌ی سالن و خدماتش.").slice(0, 160)}</span>
            </div>
            <TextInput label="عنوان در گوگل" placeholder="سالن زیبایی نگین سعادت‌آباد | کاشت ناخن و رنگ مو" maxLength={100} {...form.getInputProps("seoTitle")}
              description={<span className={classes.count} data-tone={title.length > 60 ? "warn" : undefined}>{fa(title.length)} از ۶۰ کاراکتر پیشنهادی</span>} />
            <Textarea label="توضیح در گوگل" placeholder="رزرو آنلاین کاشت ناخن، رنگ و لایت مو در سعادت‌آباد تهران." autosize minRows={3} maxRows={5} maxLength={300} {...form.getInputProps("seoDescription")}
              description={<span className={classes.count} data-tone={descTone}>{fa(text.length)} کاراکتر · بهترین بازه ۱۲۰ تا ۱۶۰</span>} />
          </SettingsSection>

          <SettingsSection title="مشخصات برای گوگل" description="کمک می‌کند گوگل بفهمد سالن شما چیست و در جستجوی انگلیسی هم پیدا شود.">
            <div className={classes.fields}>
              <TextInput label="نام لاتین" placeholder="Negin Beauty Salon" maxLength={100} styles={{ input: { direction: "ltr", textAlign: "left" } }} {...form.getInputProps("englishName")} />
              <Select label="نوع کسب‌وکار" placeholder="انتخاب کنید" data={BUSINESS_TYPES} {...form.getInputProps("businessType")} />
            </div>
          </SettingsSection>

          <SettingsSection title="تصویر اشتراک‌گذاری" description="وقتی لینک سایت در تلگرام یا واتساپ فرستاده می‌شود، این تصویر دیده می‌شود. عکس افقی، حداقل ۱۲۰۰ پیکسل عرض.">
            <div className={classes.coverRow}>
              <ImageUploading value={[]} onChange={onCover} maxNumber={1} dataURLKey="data_url" acceptType={["jpg", "jpeg", "png"]}>
                {({ onImageUpload, dragProps }) => (
                  <button type="button" className={classes.cover} onClick={onImageUpload} {...dragProps} aria-label="انتخاب تصویر اشتراک‌گذاری">
                    {cover ? <img src={cover} alt="تصویر اشتراک‌گذاری" /> : "انتخاب تصویر افقی"}
                    {coverUploading && <span className={classes.busy}><Loader size="sm" color="white" /></span>}
                  </button>
                )}
              </ImageUploading>
              <div className={classes.linkCard} aria-hidden="true">
                <div>{cover && <img src={cover} alt="" />}</div>
                <div><strong>{info?.title || "نام مجموعه"}</strong><span dir="ltr">{site ?? "your-salon.padix.ir"}</span></div>
              </div>
            </div>
            <p className={classes.hint}>تصویر از وسط به ۱۲۰۰ × ۶۳۰ برش داده می‌شود؛ همین کادر نشان می‌دهد چه بخشی دیده می‌شود.</p>
          </SettingsSection>
        </>
      )}
    </SettingsShell>
  );
}

export default EditSeo;
